import React, { useEffect, useState } from 'react'

import * as FullStory from '@fullstory/browser'
import { useQuery } from 'react-query'
import { API } from 'core/requests'
import { SPARCK_COMPANY } from 'constants/app'
import { getSpoofedCompanyFromLocalStorage } from 'utils/admin'

type CompanyContextType = {
  company: any
  companyIsLoading: boolean
  setCompany: (value: any) => void
}
const CompanyContext = React.createContext<CompanyContextType | undefined>(
  undefined,
)

type Props = {
  children: React.ReactNode
}

export const CompanyProvider = ({ children }: Props) => {
  const [company, setCompany] = useState<any>(undefined)

  const { isLoading: companyIsLoading } = useQuery(
    'getCompany',
    () =>
      API.get(
        'companies',
        getSpoofedCompanyFromLocalStorage()?.id ?? company?.id,
      ),
    {
      // Keep the query enabled (not one-shot) so company settings — e.g. a newly
      // uploaded logo — propagate to already-logged-in sessions without a logout.
      // Inside the WebToNative WebView the app stays open and never reloads, so we
      // refetch on foreground (visibilitychange/focus); that handles the common
      // "reopen the app" case instantly. The slow interval is only a low-priority
      // safety net for long-open sessions / WebViews that don't fire focus events —
      // 30 min is fine since logos/settings change rarely and there's no need to
      // reflect them on the fly (a refresh always shows the latest). staleTime
      // prevents refetch storms on rapid focus; the interval pauses while backgrounded.
      enabled: Boolean(company?.id),
      refetchOnWindowFocus: true,
      refetchInterval: 30 * 60 * 1000,
      staleTime: 60 * 1000,
      onSuccess: (response: any) => {
        setCompany(response.company)
        if (response.company) localStorage.setItem(SPARCK_COMPANY, JSON.stringify(response.company))
      },
    },
  )

  useEffect(() => {
    if (company) {
      FullStory.setUserVars({
        companyId: company!.id,
        companyName: company!.name,
      })
    }
  }, [company])

  return (
    <CompanyContext.Provider value={{ company, companyIsLoading, setCompany }}>
      {children}
    </CompanyContext.Provider>
  )
}

export const useCompany = () => React.useContext(CompanyContext)
